/**
 * HomeBannerEditor · v2.7.65+E261
 * ===============================
 * Website-Management editor for the home-page CTA banner. Autosaves (debounced
 * PUT /admin/site/home-banner). Lets admins toggle the banner on/off and edit
 * every line of copy, the phone, the button labels and the Book link — no code
 * change required.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, CloudOff, Megaphone, Phone, Link as LinkIcon } from "lucide-react";

export default function HomeBannerEditor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const timer = useRef(null);

  useEffect(() => {
    api.get("/site/home-banner")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load the CTA banner"))
      .finally(() => setLoading(false));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, []);

  const scheduleSave = useCallback((next) => {
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await adminApi.put("/admin/site/home-banner", next); setSaveState("saved"); }
      catch { setSaveState("error"); toast.error("Autosave failed — retrying on next change"); }
    }, 700);
  }, []);

  const set = useCallback((key, val) => {
    setData((cur) => {
      const next = { ...cur, [key]: val };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  if (loading || !data) {
    return <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="banner-editor-loading"><Loader2 className="h-7 w-7 animate-spin mx-auto"/></Card>;
  }

  return (
    <div className="space-y-4 mt-3" data-testid="banner-editor">
      <Card className="bg-zinc-900 border-amber-400/30 p-5" data-testid="banner-editor-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-black flex items-center gap-2 text-zinc-100"><Megaphone className="h-5 w-5 text-amber-400"/>Home CTA Banner</h3>
            <p className="text-xs text-zinc-400 mt-1">The urgent yellow banner at the very top of the home page. Autosaves — no save button.</p>
            <a href="/qr-poster" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 mt-1.5" data-testid="banner-qr-poster-link">
              <Megaphone className="h-3.5 w-3.5"/> Open printable "Scan to book" QR poster →
            </a>
          </div>
          <SaveBadge state={saveState}/>
        </div>
      </Card>

      <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-4" data-testid="banner-editor-body">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Banner</div>
          <label className="flex items-center gap-2 text-xs font-bold text-zinc-300" data-testid="banner-enabled">
            <Switch checked={!!data.enabled} onCheckedChange={(v) => set("enabled", v)}/>
            {data.enabled ? "Visible on home page" : "Hidden"}
          </label>
        </div>

        <Labeled label="Eyebrow (small red label)">
          <Input data-testid="banner-eyebrow" value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
        </Labeled>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Labeled label="Headline — part 1">
            <Input data-testid="banner-headline-1" value={data.headline_1 || ""} onChange={(e) => set("headline_1", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
          </Labeled>
          <Labeled label="Headline — highlight (red)">
            <Input data-testid="banner-headline-highlight" value={data.headline_highlight || ""} onChange={(e) => set("headline_highlight", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
          </Labeled>
          <Labeled label="Headline — part 3 (underlined)">
            <Input data-testid="banner-headline-2" value={data.headline_2 || ""} onChange={(e) => set("headline_2", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
          </Labeled>
        </div>

        <Labeled label="Subheadline">
          <textarea data-testid="banner-subhead" value={data.subheadline || ""} onChange={(e) => set("subheadline", e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y"/>
        </Labeled>
        <Labeled label="Supporting line">
          <textarea data-testid="banner-support" value={data.support_line || ""} onChange={(e) => set("support_line", e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y"/>
        </Labeled>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Phone (shown + tap-to-call)">
            <div className="relative"><Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/><Input data-testid="banner-phone" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="(580) 219-4687" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7"/></div>
          </Labeled>
          <Labeled label="Book button link (URL or /path)">
            <div className="relative"><LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/><Input data-testid="banner-book-link" value={data.book_link || ""} onChange={(e) => set("book_link", e.target.value)} placeholder="/ride/login" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7"/></div>
          </Labeled>
          <Labeled label="Call button label">
            <Input data-testid="banner-call-label" value={data.call_label || ""} onChange={(e) => set("call_label", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
          </Labeled>
          <Labeled label="Book button label">
            <Input data-testid="banner-book-label" value={data.book_label || ""} onChange={(e) => set("book_label", e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100"/>
          </Labeled>
        </div>
      </Card>
    </div>
  );
}

function SaveBadge({ state }) {
  const map = {
    idle: { cls: "text-zinc-500", node: <>Autosaves</> },
    saving: { cls: "text-amber-300", node: <><Loader2 className="h-3 w-3 animate-spin"/>Saving…</> },
    saved: { cls: "text-emerald-400", node: <><Check className="h-3 w-3"/>Saved</> },
    error: { cls: "text-rose-400", node: <><CloudOff className="h-3 w-3"/>Save failed</> },
  };
  const s = map[state] || map.idle;
  return <span className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${s.cls}`} data-testid="banner-save-state">{s.node}</span>;
}

function Labeled({ label, children }) {
  return <label className="block"><div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>{children}</label>;
}
